// ChartPage.jsx  —  KFX Journal
// TradingView chart + Quick-Log Order Panel (saves directly to DB via tradesApi)
import { useEffect, useRef, useState, useCallback } from "react";
import { tradesApi } from "../utils/api.js";
import { autoCalc, calcAmount, calcRiskAmount, fmt$, autoDetectSession } from "../utils/helpers.js";
import { PAIR_GROUPS, SESSIONS, STRATEGIES, EMOTIONS, ENTRY_TYPES, EXIT_TYPES } from "../utils/constants.js";

/* ─────────────────────────────────────────────────────────────
   CONFIG
───────────────────────────────────────────────────────────────*/
const SYMBOLS = [
  { label: "XAU/USD", value: "OANDA:XAUUSD",   pair: "XAUUSD" },
  { label: "EUR/USD", value: "FX:EURUSD",       pair: "EURUSD" },
  { label: "GBP/USD", value: "FX:GBPUSD",       pair: "GBPUSD" },
  { label: "USD/JPY", value: "FX:USDJPY",       pair: "USDJPY" },
  { label: "BTC/USD", value: "COINBASE:BTCUSD", pair: "BTCUSD" },
  { label: "NAS100",  value: "NASDAQ:NDX",      pair: "NASDAQ" },
];

const RISK_CHIPS   = ["0.5", "1", "2", "3", "5"];
const ORDER_TYPES  = ["Market", "Limit", "Stop"];
const TOPBAR_H     = 50;
const BOT_NAV_H    = 64;

/* ─── Design tokens ─────────────────────────────────────────*/
const T = {
  PAGE_BG:    "#0f0f0e",
  BAR_BG:     "#131311",
  PANEL_BG:   "#111110",
  SURFACE:    "#191917",
  SURFACE2:   "#1e1e1b",
  BORDER:     "rgba(255,255,255,0.07)",
  BORDER_MID: "rgba(255,255,255,0.12)",
  GOLD:       "#c9a84c",
  GOLD_DIM:   "rgba(201,168,76,0.14)",
  GOLD_GLOW:  "rgba(201,168,76,0.22)",
  GREEN:      "#3ecf7a",
  GREEN_DIM:  "rgba(62,207,122,0.13)",
  RED:        "#e05555",
  RED_DIM:    "rgba(224,85,85,0.13)",
  BLUE:       "#4e7fd4",
  TEXT_DIM:   "rgba(240,234,214,0.32)",
  TEXT_MID:   "rgba(240,234,214,0.60)",
  TEXT_75:    "rgba(240,234,214,0.75)",
  TEXT_FULL:  "rgba(240,234,214,0.92)",
};

/* ─────────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────────────*/
const CP_CSS = `
  *, *::before, *::after { box-sizing: border-box; }

  .cp-page {
    display: flex; flex-direction: column;
    width: 100%; height: 100%; max-height: 100%;
    overflow: hidden; background: ${T.PAGE_BG};
    position: relative;
    font-family: 'DM Sans','Inter',ui-sans-serif,system-ui,sans-serif;
  }

  /* ── TOP BAR ── */
  .cp-bar {
    display: flex; align-items: center;
    padding: 0 14px; height: 44px; flex-shrink: 0;
    border-bottom: 1px solid ${T.BORDER};
    background: ${T.BAR_BG};
    overflow-x: auto; overflow-y: hidden;
    -webkit-overflow-scrolling: touch; scrollbar-width: none;
    position: relative; gap: 0;
  }
  .cp-bar::-webkit-scrollbar { display: none; }
  .cp-bar::after {
    content:''; position:absolute; bottom:0; left:0; right:0; height:1px;
    background: linear-gradient(90deg,${T.GOLD_DIM},rgba(201,168,76,.03),transparent);
    pointer-events:none;
  }
  .cp-bar-spacer { flex: 1 1 auto; min-width: 8px; }

  /* mobile two-row bar */
  .cp-bar-mobile {
    display: none; flex-direction: column; flex-shrink: 0;
    border-bottom: 1px solid ${T.BORDER}; background: ${T.BAR_BG}; position: relative;
  }
  .cp-bar-mobile::after {
    content:''; position:absolute; bottom:0; left:0; right:0; height:1px;
    background: linear-gradient(90deg,${T.GOLD_DIM},rgba(201,168,76,.03),transparent);
    pointer-events:none;
  }
  .cp-bar-row {
    display:flex; align-items:center; height:44px; padding:0 10px;
    overflow-x:auto; overflow-y:hidden;
    -webkit-overflow-scrolling:touch; scrollbar-width:none; gap:0; flex-shrink:0;
  }
  .cp-bar-row:first-child { border-bottom: 1px solid rgba(255,255,255,0.05); }
  .cp-bar-row::-webkit-scrollbar { display:none; }

  .cp-divider {
    width:1px; height:16px; background:rgba(255,255,255,0.07);
    flex-shrink:0; margin:0 8px;
  }

  /* ── SYMBOL PILLS ── */
  .cp-sym-btn {
    display:inline-flex; align-items:center; gap:4px;
    padding:5px 11px;
    background:transparent; border:1px solid transparent; border-radius:4px;
    color:${T.TEXT_DIM};
    font-family:inherit; font-size:10.5px; font-weight:700; letter-spacing:.06em;
    cursor:pointer; white-space:nowrap; flex-shrink:0;
    transition:color .15s,background .15s,border-color .15s;
    -webkit-appearance:none; min-height:34px;
  }
  .cp-sym-btn:hover  { color:${T.TEXT_MID}; border-color:rgba(255,255,255,0.10); background:rgba(255,255,255,0.04); }
  .cp-sym-btn.active { color:${T.GOLD}; border-color:rgba(201,168,76,0.35); background:rgba(201,168,76,0.09); }

  /* ── ORDER PANEL TOGGLE ── */
  .cp-panel-toggle {
    display:inline-flex; align-items:center; gap:6px;
    padding:5px 12px;
    background:transparent; border:1px solid rgba(255,255,255,0.10); border-radius:4px;
    color:${T.TEXT_MID};
    font-family:inherit; font-size:10.5px; font-weight:700; letter-spacing:.06em;
    cursor:pointer; white-space:nowrap; flex-shrink:0;
    transition:color .15s,background .15s,border-color .15s;
    -webkit-appearance:none; min-height:34px; margin-left:4px;
  }
  .cp-panel-toggle:hover { color:${T.TEXT_FULL}; border-color:rgba(201,168,76,0.35); background:rgba(201,168,76,0.07); }
  .cp-panel-toggle.active { color:${T.GOLD}; border-color:rgba(201,168,76,0.42); background:rgba(201,168,76,0.11); }

  /* ── DEV BANNER ── */
  .cp-dev-banner {
    display:flex; align-items:center; gap:9px; padding:0 14px; height:34px;
    flex-shrink:0; background:rgba(217,79,79,0.12); border-bottom:1px solid rgba(217,79,79,0.28);
  }
  .cp-dev-dot { width:7px;height:7px;border-radius:50%;background:#E05555;flex-shrink:0;animation:cp-pulse 2.2s ease-in-out infinite; }
  @keyframes cp-pulse { 0%,100%{opacity:1}50%{opacity:.2} }
  .cp-dev-label { font-size:9px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#E05555;flex-shrink:0; }
  .cp-dev-text  { flex:1;font-size:11px;font-weight:500;color:rgba(240,234,214,0.75);overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
  .cp-dev-dismiss {
    background:transparent; border:1px solid rgba(217,79,79,0.25); border-radius:3px;
    color:rgba(224,85,85,0.55); font-size:13px; line-height:1; cursor:pointer;
    padding:2px 7px; flex-shrink:0; transition:color .15s,border-color .15s,background .15s;
    min-height:unset; font-family:inherit;
  }
  .cp-dev-dismiss:hover { color:#E05555; border-color:rgba(217,79,79,0.55); background:rgba(217,79,79,0.09); }

  /* ── BODY ROW ── */
  .cp-body { flex:1 1 0; min-height:0; display:flex; overflow:hidden; position:relative; }

  /* ── CHART ── */
  .cp-widget-wrap { flex:1 1 0; min-height:0; min-width:0; position:relative; overflow:hidden; }
  .cp-tv-container { width:100%; height:100%; position:relative; }
  .cp-tv-container .tradingview-widget-container__widget {
    position:absolute!important; inset:0!important; width:100%!important; height:100%!important; display:block!important;
  }
  .cp-tv-container iframe {
    position:absolute!important; inset:0!important; width:100%!important; height:100%!important;
    border:none; display:block;
  }

  /* ── LOADER ── */
  .cp-loader {
    position:absolute; inset:0; background:${T.PAGE_BG};
    display:flex; flex-direction:column; align-items:center; justify-content:center;
    gap:14px; z-index:5; transition:opacity .4s ease;
  }
  .cp-loader.fade-out { opacity:0; pointer-events:none; }
  .cp-loader-ring {
    width:36px;height:36px;border:2px solid rgba(201,168,76,0.12);
    border-top-color:${T.GOLD};border-radius:50%;animation:cp-spin .75s linear infinite;
  }
  @keyframes cp-spin { to { transform:rotate(360deg); } }
  .cp-loader-label { font-size:9px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:${T.TEXT_DIM}; }

  /* ══════════════════════════════════════════════════
     ORDER PANEL SHELL
  ══════════════════════════════════════════════════ */
  .cp-order-panel {
    width:0; flex-shrink:0; overflow:hidden;
    transition:width .28s cubic-bezier(0.4,0,0.2,1);
    background:${T.PANEL_BG};
    border-left:1px solid ${T.BORDER};
    display:flex; flex-direction:column;
  }
  .cp-order-panel.open { width:320px; }

  .cp-order-inner {
    width:320px; height:100%; display:flex; flex-direction:column;
    overflow-y:auto; overflow-x:hidden;
    scrollbar-width:thin; scrollbar-color:rgba(201,168,76,0.18) transparent;
    /* iOS momentum scrolling */
    -webkit-overflow-scrolling: touch;
    /* safe area bottom padding for phones with home indicator */
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  .cp-order-inner::-webkit-scrollbar { width:3px; }
  .cp-order-inner::-webkit-scrollbar-thumb { background:rgba(201,168,76,0.18); border-radius:2px; }

  /* ── Panel header ── */
  .op-header {
    display:flex; align-items:center; justify-content:space-between;
    padding:13px 16px 11px; border-bottom:1px solid ${T.BORDER}; flex-shrink:0;
    background:rgba(255,255,255,0.015);
  }
  .op-title { font-size:10px; font-weight:800; letter-spacing:.12em; text-transform:uppercase; color:${T.TEXT_FULL}; }
  .op-account-pill {
    display:flex; align-items:center; gap:5px;
    padding:3px 8px; border-radius:3px;
    background:rgba(62,207,122,0.08); border:1px solid rgba(62,207,122,0.2);
    font-size:9px; font-weight:700; letter-spacing:.06em; color:rgba(62,207,122,0.8);
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:140px;
  }
  .op-no-account-pill {
    display:flex; align-items:center; gap:5px;
    padding:3px 8px; border-radius:3px;
    background:rgba(224,85,85,0.08); border:1px solid rgba(224,85,85,0.2);
    font-size:9px; font-weight:700; letter-spacing:.06em; color:rgba(224,85,85,0.7);
  }

  /* ── Section label ── */
  .op-section { padding:13px 16px 0; flex-shrink:0; }
  .op-section-label {
    font-size:8.5px; font-weight:800; letter-spacing:.14em; text-transform:uppercase;
    color:${T.TEXT_DIM}; margin-bottom:8px; display:flex; align-items:center; gap:6px;
  }
  .op-section-label::after {
    content:''; flex:1; height:1px; background:rgba(255,255,255,0.05);
  }

  /* ── BUY/SELL tabs ── */
  .op-bstabs {
    display:flex; margin:13px 16px 0; border-radius:6px; overflow:hidden;
    border:1px solid ${T.BORDER}; flex-shrink:0;
  }
  .op-bstab {
    flex:1; padding:9px 0; border:none; background:transparent;
    font-family:inherit; font-size:12px; font-weight:800; letter-spacing:.05em;
    cursor:pointer; transition:background .15s,color .15s; color:${T.TEXT_DIM};
  }
  .op-bstab.buy.active  { background:${T.GREEN}; color:#0d0d0c; }
  .op-bstab.sell.active { background:${T.RED};   color:#fff; }
  .op-bstab:not(.active):hover { background:rgba(255,255,255,0.05); color:${T.TEXT_MID}; }

  /* ── Order type ── */
  .op-types { display:flex; gap:5px; margin:10px 16px 0; flex-shrink:0; }
  .op-type-btn {
    flex:1; padding:6px 0; border:1px solid ${T.BORDER}; border-radius:4px;
    background:transparent; font-family:inherit; font-size:9.5px; font-weight:700;
    letter-spacing:.05em; color:${T.TEXT_DIM}; cursor:pointer; transition:all .15s;
  }
  .op-type-btn:hover { color:${T.TEXT_MID}; border-color:rgba(255,255,255,0.16); }
  .op-type-btn.active { color:${T.GOLD}; border-color:rgba(201,168,76,0.38); background:rgba(201,168,76,0.08); }

  /* ── Risk chips ── */
  .op-risk-chips { display:flex; gap:4px; }
  .op-risk-chip {
    flex:1; padding:7px 0; border:1px solid ${T.BORDER}; border-radius:4px;
    background:transparent; font-family:inherit; font-size:10px; font-weight:700;
    color:${T.TEXT_DIM}; cursor:pointer; transition:all .15s; text-align:center;
  }
  .op-risk-chip:hover { color:${T.TEXT_MID}; border-color:rgba(255,255,255,0.16); }
  .op-risk-chip.active { color:${T.GOLD}; border-color:rgba(201,168,76,0.38); background:rgba(201,168,76,0.08); }

  /* ── Field / input ── */
  .op-field { display:flex; flex-direction:column; gap:4px; }
  .op-field-label {
    font-size:8.5px; font-weight:700; letter-spacing:.10em;
    text-transform:uppercase; color:${T.TEXT_DIM};
  }
  .op-input-wrap {
    display:flex; align-items:stretch; border:1px solid ${T.BORDER}; border-radius:5px;
    background:${T.SURFACE}; overflow:hidden; transition:border-color .15s;
  }
  .op-input-wrap:focus-within { border-color:rgba(201,168,76,0.38); }
  .op-input {
    flex:1; padding:8px 10px; background:transparent; border:none; outline:none;
    font-family:inherit; font-size:12px; font-weight:500; color:${T.TEXT_FULL}; min-width:0;
  }
  .op-input::placeholder { color:${T.TEXT_DIM}; }
  .op-input-unit {
    padding:0 9px; font-size:9.5px; font-weight:700; color:${T.TEXT_DIM};
    border-left:1px solid ${T.BORDER}; display:flex; align-items:center;
    white-space:nowrap; flex-shrink:0; background:rgba(255,255,255,0.02); min-height:34px;
  }
  select.op-input { cursor:pointer; }
  select.op-input option { background:#1a1a17; }

  /* ── Stat row ── */
  .op-stat-row {
    display:flex; justify-content:space-between; align-items:center;
    padding:6px 10px; background:${T.SURFACE}; border:1px solid ${T.BORDER}; border-radius:4px;
  }
  .op-stat-key { font-size:9.5px; font-weight:600; color:${T.TEXT_DIM}; }
  .op-stat-val { font-size:11px; font-weight:700; color:${T.TEXT_FULL}; font-variant-numeric:tabular-nums; }
  .op-stat-val.gold  { color:${T.GOLD};  }
  .op-stat-val.green { color:${T.GREEN}; }
  .op-stat-val.red   { color:${T.RED};   }
  .op-stat-val.blue  { color:${T.BLUE};  }

  /* ── RR badge ── */
  .op-rr-badge {
    display:flex; align-items:center; gap:8px; justify-content:center;
    padding:9px 12px;
    background:rgba(201,168,76,0.06); border:1px solid rgba(201,168,76,0.16); border-radius:5px;
  }
  .op-rr-label { font-size:9px; font-weight:700; letter-spacing:.10em; text-transform:uppercase; color:${T.TEXT_DIM}; }
  .op-rr-val   { font-size:18px; font-weight:800; color:${T.GOLD}; font-variant-numeric:tabular-nums; line-height:1; }
  .op-rr-unit  { font-size:9px; font-weight:600; color:${T.TEXT_DIM}; }

  /* ── Toggle switch ── */
  .op-toggle-row {
    display:flex; align-items:center; justify-content:space-between;
  }
  .op-toggle-label { font-size:10.5px; font-weight:600; color:${T.TEXT_MID}; }
  .op-switch { position:relative; width:34px; height:18px; cursor:pointer; flex-shrink:0; }
  .op-switch input { opacity:0; width:0; height:0; position:absolute; }
  .op-switch-track {
    position:absolute; inset:0; border-radius:9px;
    background:rgba(255,255,255,0.07); border:1px solid rgba(255,255,255,0.09);
    transition:background .2s,border-color .2s;
  }
  .op-switch input:checked ~ .op-switch-track { background:rgba(201,168,76,0.28); border-color:rgba(201,168,76,0.45); }
  .op-switch-knob {
    position:absolute; top:2px; left:2px;
    width:12px; height:12px; border-radius:50%;
    background:rgba(240,234,214,0.38); transition:transform .2s,background .2s;
  }
  .op-switch input:checked ~ .op-switch-knob { transform:translateX(16px); background:${T.GOLD}; }

  /* ── Checkbox row ── */
  .op-checkbox-row { display:flex; align-items:center; gap:8px; }
  .op-checkbox {
    width:14px; height:14px; border:1px solid rgba(255,255,255,0.16); border-radius:3px;
    background:transparent; cursor:pointer; appearance:none; -webkit-appearance:none;
    flex-shrink:0; transition:background .15s,border-color .15s; position:relative;
  }
  .op-checkbox:checked { background:${T.GOLD}; border-color:${T.GOLD}; }
  .op-checkbox:checked::after {
    content:'✓'; position:absolute; top:50%; left:50%;
    transform:translate(-50%,-50%); font-size:9px; color:#0f0f0e; font-weight:900;
  }
  .op-checkbox-label { font-size:10.5px; font-weight:600; color:${T.TEXT_MID}; cursor:pointer; }

  /* ── Emotion chips ── */
  .op-emotion-grid { display:flex; flex-wrap:wrap; gap:5px; }
  .op-em-chip {
    padding:4px 9px; border:1px solid rgba(255,255,255,0.09); border-radius:3px;
    background:rgba(255,255,255,0.03); font-family:inherit; font-size:9.5px;
    font-weight:600; color:${T.TEXT_DIM}; cursor:pointer; transition:all .15s; white-space:nowrap;
  }
  .op-em-chip:hover { color:${T.TEXT_MID}; border-color:rgba(255,255,255,0.18); }
  .op-em-chip.active { color:${T.GOLD}; border-color:rgba(201,168,76,0.4); background:rgba(201,168,76,0.09); }

  /* ── Star rating ── */
  .op-stars { display:flex; gap:3px; margin-top:3px; }
  .op-star {
    background:none; border:none; cursor:pointer; padding:0;
    font-size:16px; line-height:1; transition:color .12s;
  }

  /* ── Separator ── */
  .op-hr { margin:13px 16px 0; border:none; border-top:1px solid ${T.BORDER}; flex-shrink:0; }

  /* ── Submit button ── */
  .op-submit-wrap { padding:14px 16px 16px; flex-shrink:0; }
  .op-submit {
    width:100%; padding:12px 0; border:none; border-radius:6px;
    font-family:inherit; font-size:12.5px; font-weight:800; letter-spacing:.07em;
    cursor:pointer; transition:opacity .15s,transform .1s;
    display:flex; align-items:center; justify-content:center; gap:8px;
  }
  .op-submit:disabled { opacity:.5; cursor:not-allowed; transform:none!important; }
  .op-submit:not(:disabled):active { transform:scale(0.98); }
  .op-submit.buy  { background:${T.GREEN}; color:#0d0d0c; }
  .op-submit.sell { background:${T.RED};   color:#fff; }
  .op-submit-note { margin-top:7px; text-align:center; font-size:8.5px; font-weight:600; letter-spacing:.05em; color:${T.TEXT_DIM}; }

  /* ── Success / error toast inside panel ── */
  .op-panel-toast {
    margin:0 16px 13px; padding:9px 12px; border-radius:5px;
    font-size:10.5px; font-weight:600; line-height:1.5; display:flex; align-items:flex-start; gap:8px;
  }
  .op-panel-toast.success { background:rgba(62,207,122,0.1); border:1px solid rgba(62,207,122,0.25); color:rgba(62,207,122,0.9); }
  .op-panel-toast.error   { background:rgba(224,85,85,0.1);  border:1px solid rgba(224,85,85,0.25);  color:rgba(224,85,85,0.9);  }

  /* ── Warn inline ── */
  .op-warn {
    font-size:8.5px; color:${T.RED}; font-weight:600; margin-top:3px; display:flex; align-items:center; gap:4px;
  }

  /* ── Grid helpers ── */
  .op-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .op-stack  { display:flex; flex-direction:column; gap:8px; }

  /* ══════════════════════════════════════════════════
     RESPONSIVE
  ══════════════════════════════════════════════════ */

  /* ── Tablet (≤960px): fix page height, shrink panel ── */
  @media (max-width: 960px) {
    .cp-page {
      position: fixed !important;
      top: ${TOPBAR_H}px !important;
      left: 0 !important; right: 0 !important;
      bottom: ${BOT_NAV_H}px !important;
      height: auto !important; max-height: none !important;
      width: 100% !important; z-index: 10;
    }
    .cp-order-panel.open { width: 280px; }
    .cp-order-inner      { width: 280px; }
  }

  /* ── Mobile (≤680px): two-row bar + overlay panel ── */
  @media (max-width: 680px) {
    /* show dim overlay behind panel */
    .cp-panel-overlay { display: block !important; }

    /* swap bars */
    .cp-bar        { display: none; }
    .cp-bar-mobile { display: flex; }

    /* bar buttons larger tap targets */
    .cp-sym-btn        { padding: 6px 10px; font-size: 10.5px; min-height: 40px; }
    .cp-panel-toggle   { min-height: 40px; font-size: 10.5px; padding: 6px 12px; }
    .cp-dev-text       { font-size: 10.5px; }

    /* panel slides over chart as a full-height overlay */
    .cp-order-panel {
      position: absolute !important;
      top: 0; right: 0; bottom: 0;
      z-index: 20;
      border-left: 1px solid ${T.BORDER};
      box-shadow: -10px 0 40px rgba(0,0,0,0.7);
      /* start collapsed */
      width: 0 !important;
      transition: width .28s cubic-bezier(0.4,0,0.2,1) !important;
    }
    .cp-order-panel.open { width: min(88vw, 340px) !important; }

    /* inner must match panel width exactly */
    .cp-order-inner {
      width: min(88vw, 340px);
      /* larger touch targets inside panel */
    }

    /* panel tabs — bigger tap area */
    .cp-order-inner [style*="flex-direction: column"][style*="align-items: center"] {
      padding: 10px 4px 9px;
    }

    /* inputs & selects — 44px min touch target */
    .op-input          { padding: 10px 11px; font-size: 13px; min-height: 44px; }
    .op-input-unit     { min-height: 44px; font-size: 10px; padding: 0 10px; }
    .op-input-wrap     { border-radius: 6px; }

    /* risk chips */
    .op-risk-chip      { padding: 9px 0; font-size: 11px; min-height: 40px; border-radius: 5px; }

    /* order type buttons */
    .op-type-btn       { padding: 8px 0; font-size: 10.5px; min-height: 38px; }

    /* buy/sell tabs */
    .op-bstab          { padding: 11px 0; font-size: 13px; }

    /* section labels */
    .op-section        { padding: 14px 14px 0; }
    .op-section-label  { font-size: 9px; margin-bottom: 9px; }

    /* stat rows */
    .op-stat-row       { padding: 8px 12px; }
    .op-stat-key       { font-size: 10px; }
    .op-stat-val       { font-size: 12px; }

    /* grid: single column on very narrow */
    .op-grid-2         { grid-template-columns: 1fr 1fr; gap: 8px; }

    /* toggles */
    .op-toggle-label   { font-size: 11.5px; }
    .op-switch         { width: 38px; height: 20px; }
    .op-switch-knob    { width: 14px; height: 14px; }
    .op-switch input:checked ~ .op-switch-knob { transform: translateX(18px); }

    /* emotion chips */
    .op-em-chip        { padding: 6px 11px; font-size: 10.5px; }

    /* stars */
    .op-star           { font-size: 20px; }

    /* submit */
    .op-submit         { padding: 14px 0; font-size: 13px; border-radius: 7px; }
    .op-submit-wrap    { padding: 14px 14px 18px; }
    .op-submit-note    { font-size: 9px; }

    /* separator */
    .op-hr             { margin: 14px 14px 0; }

    /* panel toast */
    .op-panel-toast    { margin: 0 14px 14px; font-size: 11px; }

    /* header */
    .op-header         { padding: 14px 14px 12px; }
    .op-title          { font-size: 10.5px; }

    /* RR badge */
    .op-rr-badge       { padding: 10px 14px; }
    .op-rr-val         { font-size: 20px; }
  }

  /* ── Very small phones (≤400px): full-width panel, single-col grid ── */
  @media (max-width: 400px) {
    .cp-sym-btn               { padding: 5px 8px; font-size: 10px; }
    .cp-order-panel.open      { width: 100vw !important; }
    .cp-order-inner           { width: 100vw; }
    .op-grid-2                { grid-template-columns: 1fr; }
  }
`;

function injectCSS() {
  if (document.getElementById("cp-styles")) return;
  const el = document.createElement("style");
  el.id = "cp-styles";
  el.textContent = CP_CSS;
  document.head.appendChild(el);
}

/* ─────────────────────────────────────────────────────────────
   SMALL HELPERS
───────────────────────────────────────────────────────────────*/
function isWeekend(dateStr) {
  if (!dateStr) return false;
  const [y, m, d] = dateStr.split("-").map(Number);
  const day = new Date(y, m - 1, d).getDay();
  return day === 0 || day === 6;
}

function Switch({ checked, onChange }) {
  return (
    <label className="op-switch">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="op-switch-track" />
      <span className="op-switch-knob" />
    </label>
  );
}

function StarRating({ value, onChange, color = "var(--gold)" }) {
  return (
    <div className="op-stars">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" className="op-star"
          style={{ color: n <= value ? color : "rgba(255,255,255,0.1)" }}
          onClick={() => onChange(n)}>★</button>
      ))}
    </div>
  );
}
const PANEL_EMPTY = {
  dir: "BUY",
  orderType: "Market",
  pair: "",
  riskPct: "1",
  entry: "",
  sl: "",
  tp: "",
  lotSize: "",
  date: new Date().toISOString().slice(0, 10),
  time: new Date().toTimeString().slice(0, 5),
  exitDate: "",
  exitTime: "",
  session: "",
  strat: "",
  entryType: "",
  exitType: "",
  emotion: "",
  confidence: 0,
  discipline: 0,
  stressLevel: 0,
  notes: "",
  movedSl: false,
  partialClose: false,
  heldToTp: false,
  newsPresent: false,
  confluences: [],
  ruleChecks: [],
  actualEntry: "",
  actualExit: "",
  mfe: "",
  mae: "",
  htfBias: "",
  htfTf: "H4",
  entryTf: "M15",
  marketCondition: "",
  imgBefore: "",
  imgAfter: "",
  setupGrade: "",
  followedPlan: "",
  plannedRR: "",
  riskAmount: "",
  accountSize: "",
  status: "OPEN",
};

/* ─── Panel tabs ─────────────────────────────────────────────*/
const PTABS = [
  { id: "plan",    label: "Plan",    icon: "📐" },
  { id: "result",  label: "Result",  icon: "✅" },
  { id: "context", label: "Context", icon: "🌍" },
  { id: "mindset", label: "Mindset", icon: "🧠" },
];

/* ─────────────────────────────────────────────────────────────
   ORDER PANEL COMPONENT
───────────────────────────────────────────────────────────────*/
function OrderPanel({ open, activeAccount, chartSymbol }) {
  const [form,   setForm]   = useState({ ...PANEL_EMPTY });
  const [ptab,   setPtab]   = useState("plan");
  const [saving, setSaving] = useState(false);
  const [toast,  setToast]  = useState(null); // { type: 'success'|'error', msg }
  const [errors, setErrors] = useState([]);

  const set = (k, v) => { setErrors([]); setForm(f => ({ ...f, [k]: v })); };

  // Auto-populate pair from chart symbol when panel opens / symbol changes
  useEffect(() => {
    if (chartSymbol && !form.pair) {
      set("pair", chartSymbol.pair || "");
    }
  }, [chartSymbol, open]); // eslint-disable-line

  // Auto-detect session from time
  useEffect(() => {
    if (form.time && !form.session) {
      const s = autoDetectSession(form.time);
      if (s) set("session", s);
    }
  }, [form.time]); // eslint-disable-line

  // Account balance auto-fill
  const accountBalance = String(activeAccount?.balance || "");
  useEffect(() => {
    if (accountBalance) set("accountSize", accountBalance);
  }, [accountBalance]); // eslint-disable-line

  // ── Live calculations ──
  const calc      = autoCalc(form.entry, form.sl, form.tp, form.dir, form.pair);
  const estPnL    = calcAmount(form.entry, form.sl, form.tp, form.dir, form.lotSize, form.pair);
  const riskAmt   = calcRiskAmount(form.entry, form.sl, form.lotSize, form.pair);
  const riskPctCalc = riskAmt && form.accountSize
    ? ((riskAmt / parseFloat(form.accountSize)) * 100).toFixed(2)
    : null;

  // Actual RR (result tab)
  const actualRR = (() => {
    const e  = parseFloat(form.actualEntry || form.entry);
    const ex = parseFloat(form.actualExit);
    const sl = parseFloat(form.sl);
    if (!e || !ex || !sl) return null;
    const risk = Math.abs(e - sl);
    const rew  = Math.abs(ex - e);
    return risk > 0 ? (rew / risk).toFixed(2) : null;
  })();

  // Price direction warnings
  const priceWarns = (() => {
    const e = parseFloat(form.entry), s = parseFloat(form.sl), t = parseFloat(form.tp);
    const w = [];
    if (form.dir === "BUY"  && e && s && s >= e) w.push("SL must be below entry for BUY");
    if (form.dir === "BUY"  && e && t && t <= e) w.push("TP must be above entry for BUY");
    if (form.dir === "SELL" && e && s && s <= e) w.push("SL must be above entry for SELL");
    if (form.dir === "SELL" && e && t && t >= e) w.push("TP must be below entry for SELL");
    return w;
  })();

  // ── Reset form ──
  const resetForm = () => {
    setForm({
      ...PANEL_EMPTY,
      accountSize: accountBalance,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().slice(0, 5),
      pair: chartSymbol?.pair || "",
    });
    setPtab("plan");
    setErrors([]);
  };

  // ── Save ──
  const handleSave = async () => {
    const errs = [];
    if (!form.pair) errs.push("Pair is required.");
    if (!form.dir)  errs.push("Direction is required.");
    if (!form.date) errs.push("Date is required.");
    if (isWeekend(form.date)) errs.push("Date is a weekend — markets are closed.");
    if (priceWarns.length) priceWarns.forEach(w => errs.push(w));
    if (errs.length) { setErrors(errs); return; }

    setSaving(true);
    setToast(null);

    try {
      const result = estPnL !== null ? estPnL : 0;
      const resolvedExitType = form.exitType || "";

      const payload = {
        ...form,
        accountId:   activeAccount?.id || null,
        pips:        result >= 0 ? Math.abs(parseFloat(calc.pips) || 0) : -(Math.abs(parseFloat(calc.pips) || 0)),
        rr:          calc.rr || "0.00",
        actualRR:    actualRR || calc.rr || "0.00",
        result,
        lotSize:     parseFloat(form.lotSize) || 0,
        riskAmount:  riskAmt || null,
        riskPct:     riskPctCalc || form.riskPct,
        duration:    "",
        img:         form.imgBefore,
        followedPlan: false,
        setupGrade:   "",
        exitType:    resolvedExitType,
        ruleChecks:  [],
        htfTf:       form.htfTf || "H4",
        entryTf:     form.entryTf || "M15",
        plannedRR:   calc.rr || null,
        status:      form.exitTime ? "CLOSED" : "OPEN",
      };

      await tradesApi.create(payload);

      setToast({ type: "success", msg: `${form.dir} ${form.pair} logged to journal ✓` });
      window.dispatchEvent(new CustomEvent("kfx:trade-logged", { detail: { trade: payload } }));
      setTimeout(() => {
        setToast(null);
        resetForm();
      }, 3000);
    } catch (err) {
      setToast({ type: "error", msg: err.message || "Failed to save. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const symLabel = form.pair || "—";

  return (
    <div className={`cp-order-panel${open ? " open" : ""}`}>
      <div className="cp-order-inner">

        {/* ── Header ── */}
        <div className="op-header">
          <span className="op-title">Quick Log</span>
          {activeAccount
            ? (
              <div className="op-account-pill">
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.GREEN, display: "inline-block", flexShrink: 0 }} />
                {activeAccount.name}
              </div>
            )
            : <div className="op-no-account-pill">⚠ No account</div>
          }
        </div>

        {/* ── Panel tabs ── */}
        <div style={{
          display: "flex", borderBottom: `1px solid ${T.BORDER}`,
          background: "rgba(255,255,255,0.01)", flexShrink: 0,
        }}>
          {PTABS.map(t => (
            <button key={t.id} onClick={() => setPtab(t.id)} style={{
              flex: 1, padding: "9px 4px 8px", border: "none", background: "none",
              borderBottom: ptab === t.id ? `2px solid ${T.GOLD}` : "2px solid transparent",
              color: ptab === t.id ? T.GOLD : T.TEXT_DIM,
              font: "inherit", fontSize: 8.5, fontWeight: 700, letterSpacing: ".08em",
              textTransform: "uppercase", cursor: "pointer", transition: "all .15s",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
            }}>
              <span style={{ fontSize: 13, lineHeight: 1 }}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Toast ── */}
        {toast && (
          <div className={`op-panel-toast ${toast.type}`} style={{ marginTop: 13 }}>
            <span>{toast.type === "success" ? "✓" : "⚠"}</span>
            <span>{toast.msg}</span>
          </div>
        )}

        {/* ── Validation errors ── */}
        {errors.length > 0 && (
          <div style={{
            margin: "13px 16px 0", padding: "9px 12px",
            background: "rgba(224,85,85,0.08)", border: "1px solid rgba(224,85,85,0.22)", borderRadius: 5,
          }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: T.RED, letterSpacing: ".1em", marginBottom: 5 }}>
              FIX BEFORE SAVING
            </div>
            {errors.map((e, i) => (
              <div key={i} style={{ fontSize: 10, color: "rgba(240,200,200,0.75)", lineHeight: 1.6 }}>· {e}</div>
            ))}
          </div>
        )}

        {/* ══════════════════════════════════════════
            TAB: PLAN
        ══════════════════════════════════════════ */}
        {ptab === "plan" && (
          <>
            {/* BUY / SELL */}
            <div className="op-bstabs">
              <button className={`op-bstab buy${form.dir === "BUY" ? " active" : ""}`} onClick={() => set("dir", "BUY")}>▲ BUY</button>
              <button className={`op-bstab sell${form.dir === "SELL" ? " active" : ""}`} onClick={() => set("dir", "SELL")}>▼ SELL</button>
            </div>

            {/* Order type */}
            <div className="op-types">
              {ORDER_TYPES.map(t => (
                <button key={t} className={`op-type-btn${form.orderType === t ? " active" : ""}`} onClick={() => set("orderType", t)}>{t}</button>
              ))}
            </div>

            {/* Pair + Date/Time */}
            <div className="op-section">
              <div className="op-section-label">Trade Details</div>
              <div className="op-grid-2" style={{ marginBottom: 8 }}>
                <div className="op-field">
                  <div className="op-field-label">Pair *</div>
                  <div className="op-input-wrap">
                    <select className="op-input" value={form.pair} onChange={e => set("pair", e.target.value)}>
                      <option value="">-- Select --</option>
                      {(() => {
                        // Shape 1: array of { label, pairs } objects  → [{ label: "Forex", pairs: ["EURUSD",...] }]
                        if (Array.isArray(PAIR_GROUPS) && PAIR_GROUPS.length > 0 && typeof PAIR_GROUPS[0] === "object" && PAIR_GROUPS[0] !== null && "pairs" in PAIR_GROUPS[0]) {
                          return PAIR_GROUPS.map(grp =>
                            Array.isArray(grp.pairs) ? (
                              <optgroup key={grp.label || grp.pairs[0]} label={grp.label || ""}>
                                {grp.pairs.map(p => <option key={p} value={p}>{p}</option>)}
                              </optgroup>
                            ) : null
                          );
                        }
                        // Shape 2: plain object  → { Forex: ["EURUSD",...] }
                        if (PAIR_GROUPS && typeof PAIR_GROUPS === "object" && !Array.isArray(PAIR_GROUPS)) {
                          return Object.entries(PAIR_GROUPS).map(([grp, pairs]) =>
                            Array.isArray(pairs) ? (
                              <optgroup key={grp} label={grp}>
                                {pairs.map(p => <option key={p} value={p}>{p}</option>)}
                              </optgroup>
                            ) : null
                          );
                        }
                        // Shape 3: flat array of strings  → ["EURUSD","GBPUSD",...]
                        if (Array.isArray(PAIR_GROUPS)) {
                          return PAIR_GROUPS.map(p => typeof p === "string" ? <option key={p} value={p}>{p}</option> : null);
                        }
                        // Fallback: use local SYMBOLS constant
                        return SYMBOLS.map(s => <option key={s.pair} value={s.pair}>{s.pair}</option>);
                      })()}
                    </select>
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Date *</div>
                  <div className="op-input-wrap">
                    <input className="op-input" type="date"
                      value={form.date}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={e => set("date", e.target.value)}
                      style={isWeekend(form.date) ? { color: T.RED } : {}} />
                  </div>
                  {isWeekend(form.date) && <div className="op-warn">⛔ Weekend</div>}
                </div>
              </div>
              <div className="op-grid-2" style={{ marginBottom: 8 }}>
                <div className="op-field">
                  <div className="op-field-label">Entry Time</div>
                  <div className="op-input-wrap">
                    <input className="op-input" type="time" value={form.time} onChange={e => set("time", e.target.value)} />
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Session</div>
                  <div className="op-input-wrap">
                    <select className="op-input" value={form.session} onChange={e => set("session", e.target.value)}>
                      <option value="">Auto</option>
                      {(SESSIONS || ["Asian", "London", "New York", "London-NY"]).map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <hr className="op-hr" />

            {/* Price levels */}
            <div className="op-section">
              <div className="op-section-label">Price Levels</div>
              <div className="op-grid-2" style={{ marginBottom: 8 }}>
                {/* Entry price — always required for calculations */}
                <div className="op-field" style={{ gridColumn: "span 2" }}>
                  <div className="op-field-label">
                    {form.orderType === "Market" ? "Entry Price (for calculations)" : "Entry Price"}
                  </div>
                  <div className="op-input-wrap" style={form.entry ? { borderColor: "rgba(201,168,76,0.35)" } : {}}>
                    <input className="op-input" type="number" step="any"
                      value={form.entry}
                      onChange={e => set("entry", e.target.value)}
                      placeholder={form.orderType === "Market" ? "Current market price (e.g. 2345.50)" : "Limit / Stop price"}
                      style={{ color: form.entry ? T.GOLD : undefined }}
                    />
                    <span className="op-input-unit">{symLabel}</span>
                  </div>
                  {form.orderType === "Market" && !form.entry && (
                    <div style={{ fontSize: 8, color: T.TEXT_DIM, marginTop: 3 }}>
                      ⚡ Enter current price so R:R and P&L can calculate
                    </div>
                  )}
                </div>
                <div className="op-field">
                  <div className="op-field-label">Stop Loss</div>
                  <div className="op-input-wrap" style={form.sl ? { borderColor: "rgba(224,85,85,0.35)" } : {}}>
                    <input className="op-input" type="number" step="any" value={form.sl} onChange={e => set("sl", e.target.value)} placeholder="0.00000"
                      style={{ color: form.sl ? T.RED : undefined }} />
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Take Profit</div>
                  <div className="op-input-wrap" style={form.tp ? { borderColor: "rgba(62,207,122,0.35)" } : {}}>
                    <input className="op-input" type="number" step="any" value={form.tp} onChange={e => set("tp", e.target.value)} placeholder="0.00000"
                      style={{ color: form.tp ? T.GREEN : undefined }} />
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Lot Size</div>
                  <div className="op-input-wrap">
                    <input className="op-input" type="number" step="0.01" min="0.01" value={form.lotSize} onChange={e => set("lotSize", e.target.value)} placeholder="0.01" />
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Strategy</div>
                  <div className="op-input-wrap">
                    <select className="op-input" value={form.strat} onChange={e => set("strat", e.target.value)}>
                      <option value="">--</option>
                      {(STRATEGIES || []).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              {priceWarns.map((w, i) => <div key={i} className="op-warn" style={{ marginBottom: 4 }}>⚠ {w}</div>)}
            </div>

            <hr className="op-hr" />

            {/* Risk */}
            <div className="op-section">
              <div className="op-section-label">Risk</div>
              <div className="op-risk-chips" style={{ marginBottom: 8 }}>
                {RISK_CHIPS.map(c => (
                  <button key={c} className={`op-risk-chip${form.riskPct === c ? " active" : ""}`} onClick={() => set("riskPct", c)}>{c}%</button>
                ))}
              </div>
              {/* Account balance (read-only, auto-filled) */}
              <div className="op-field" style={{ marginBottom: 8 }}>
                <div className="op-field-label">Account Balance</div>
                <div className="op-input-wrap" style={{ borderColor: "rgba(62,207,122,0.2)" }}>
                  <input className="op-input" type="number" value={form.accountSize} readOnly
                    style={{ cursor: "not-allowed", color: T.TEXT_MID }} />
                  <span className="op-input-unit">USD</span>
                </div>
                {activeAccount && (
                  <div style={{ fontSize: 8, color: "rgba(62,207,122,0.55)", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 4, height: 4, borderRadius: "50%", background: T.GREEN, display: "inline-block" }} />
                    {activeAccount.name}
                  </div>
                )}
              </div>
            </div>

            <hr className="op-hr" />

            {/* Live calcs */}
            <div className="op-section">
              <div className="op-section-label">Calculated</div>
              {!form.entry ? (
                <div style={{
                  padding: "10px 12px", background: T.SURFACE,
                  border: `1px solid ${T.BORDER}`, borderRadius: 5,
                  fontSize: 10, color: T.TEXT_DIM, lineHeight: 1.6,
                }}>
                  ⚡ Enter an <strong style={{ color: T.GOLD }}>Entry Price</strong> above to see live R:R, P&L and pip distance calculations.
                </div>
              ) : (
                <div className="op-stack" style={{ marginBottom: 0 }}>
                  <div className="op-stat-row">
                    <span className="op-stat-key">Distance (Pips)</span>
                    <span className="op-stat-val">{(calc.pips && parseFloat(calc.pips) !== 0) ? calc.pips : "—"}</span>
                  </div>
                  <div className="op-stat-row">
                    <span className="op-stat-key">Planned R:R</span>
                    <span className="op-stat-val gold">{(calc.rr && parseFloat(calc.rr) > 0) ? `${calc.rr}R` : "—"}</span>
                  </div>
                  <div className="op-stat-row">
                    <span className="op-stat-key">Est. P&L</span>
                    <span className={`op-stat-val ${estPnL > 0 ? "green" : estPnL < 0 ? "red" : ""}`}>
                      {estPnL !== null ? fmt$(estPnL) : "Need SL + TP + Lot"}
                    </span>
                  </div>
                  <div className="op-stat-row">
                    <span className="op-stat-key">Risk of Account</span>
                    <span className={`op-stat-val ${parseFloat(riskPctCalc) > 2 ? "red" : riskPctCalc ? "blue" : ""}`}>
                      {riskPctCalc ? `${riskPctCalc}%` : "Need SL + Lot"}
                    </span>
                  </div>
                  {riskAmt != null && riskAmt > 0 && (
                    <div className="op-stat-row">
                      <span className="op-stat-key">Risk Amount</span>
                      <span className="op-stat-val gold">${riskAmt.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Entry type */}
            <div className="op-section" style={{ marginBottom: 13 }}>
              <div className="op-field" style={{ marginTop: 8 }}>
                <div className="op-field-label">Entry Type</div>
                <div className="op-input-wrap">
                  <select className="op-input" value={form.entryType} onChange={e => set("entryType", e.target.value)}>
                    <option value="">--</option>
                    {(ENTRY_TYPES || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB: RESULT
        ══════════════════════════════════════════ */}
        {ptab === "result" && (
          <>
            <div className="op-section">
              <div className="op-section-label">Actual Prices</div>
              <div className="op-grid-2" style={{ marginBottom: 8 }}>
                <div className="op-field">
                  <div className="op-field-label">Actual Entry</div>
                  <div className="op-input-wrap">
                    <input className="op-input" type="number" step="any" value={form.actualEntry} onChange={e => set("actualEntry", e.target.value)} placeholder="0.00000" />
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Actual Exit</div>
                  <div className="op-input-wrap">
                    <input className="op-input" type="number" step="any" value={form.actualExit} onChange={e => set("actualExit", e.target.value)} placeholder="0.00000" />
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Exit Date</div>
                  <div className="op-input-wrap">
                    <input className="op-input" type="date" value={form.exitDate} min={form.date || undefined} max={new Date().toISOString().slice(0, 10)} onChange={e => set("exitDate", e.target.value)} />
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Exit Time</div>
                  <div className="op-input-wrap">
                    <input className="op-input" type="time" value={form.exitTime} onChange={e => set("exitTime", e.target.value)} />
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">MFE</div>
                  <div className="op-input-wrap">
                    <input className="op-input" type="number" step="any" value={form.mfe} onChange={e => set("mfe", e.target.value)} placeholder="best price" />
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">MAE</div>
                  <div className="op-input-wrap">
                    <input className="op-input" type="number" step="any" value={form.mae} onChange={e => set("mae", e.target.value)} placeholder="worst price" />
                  </div>
                </div>
              </div>
            </div>

            <hr className="op-hr" />

            <div className="op-section">
              <div className="op-section-label">Outcome</div>
              {actualRR && (
                <div className="op-rr-badge" style={{ marginBottom: 8 }}>
                  <span className="op-rr-label">Actual R:R</span>
                  <span className="op-rr-val">1 : {actualRR}</span>
                  <span className="op-rr-unit">R</span>
                </div>
              )}
              <div className="op-field" style={{ marginBottom: 8 }}>
                <div className="op-field-label">Exit Type</div>
                <div className="op-input-wrap">
                  <select className="op-input" value={form.exitType} onChange={e => set("exitType", e.target.value)}>
                    <option value="">--</option>
                    {(EXIT_TYPES || ["TP Hit", "SL Hit", "Break-Even", "Manual"]).map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="op-stack" style={{ marginBottom: 8 }}>
                <div className="op-toggle-row">
                  <span className="op-toggle-label">Moved SL</span>
                  <Switch checked={form.movedSl} onChange={v => set("movedSl", v)} />
                </div>
                <div className="op-toggle-row">
                  <span className="op-toggle-label">Partial Close</span>
                  <Switch checked={form.partialClose} onChange={v => set("partialClose", v)} />
                </div>
                <div className="op-toggle-row">
                  <span className="op-toggle-label">Held to TP</span>
                  <Switch checked={form.heldToTp} onChange={v => set("heldToTp", v)} />
                </div>
              </div>
            </div>

            <hr className="op-hr" />

            <div className="op-section" style={{ marginBottom: 13 }}>
              <div className="op-section-label">Notes</div>
              <textarea
                value={form.notes}
                onChange={e => set("notes", e.target.value)}
                placeholder="What happened? Anything unexpected?"
                style={{
                  width: "100%", padding: "9px 10px",
                  background: T.SURFACE, border: `1px solid ${T.BORDER}`, borderRadius: 5,
                  color: T.TEXT_FULL, font: "inherit", fontSize: 11, lineHeight: 1.6,
                  resize: "vertical", minHeight: 72, outline: "none",
                  transition: "border-color .15s",
                }}
                onFocus={e => { e.target.style.borderColor = "rgba(201,168,76,0.38)"; }}
                onBlur={e => { e.target.style.borderColor = T.BORDER; }}
              />
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB: CONTEXT
        ══════════════════════════════════════════ */}
        {ptab === "context" && (
          <>
            <div className="op-section">
              <div className="op-section-label">Timeframes</div>
              <div className="op-grid-2" style={{ marginBottom: 8 }}>
                <div className="op-field">
                  <div className="op-field-label">HTF Bias</div>
                  <div className="op-input-wrap">
                    <select className="op-input" value={form.htfBias} onChange={e => set("htfBias", e.target.value)}>
                      <option value="">--</option>
                      {["Bullish", "Bearish", "Neutral", "Range"].map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">HTF Timeframe</div>
                  <div className="op-input-wrap">
                    <select className="op-input" value={form.htfTf} onChange={e => set("htfTf", e.target.value)}>
                      {["M15","M30","H1","H4","D1","W1"].map(tf => <option key={tf} value={tf}>{tf}</option>)}
                    </select>
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Entry TF</div>
                  <div className="op-input-wrap">
                    <select className="op-input" value={form.entryTf} onChange={e => set("entryTf", e.target.value)}>
                      {["M1","M5","M15","M30","H1","H4"].map(tf => <option key={tf} value={tf}>{tf}</option>)}
                    </select>
                  </div>
                </div>
                <div className="op-field">
                  <div className="op-field-label">Market Condition</div>
                  <div className="op-input-wrap">
                    <select className="op-input" value={form.marketCondition} onChange={e => set("marketCondition", e.target.value)}>
                      <option value="">--</option>
                      {(["Trending", "Ranging", "Volatile", "Consolidating"]).map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <hr className="op-hr" />

            <div className="op-section">
              <div className="op-section-label">News</div>
              <div className="op-toggle-row" style={{ marginBottom: 8 }}>
                <span className="op-toggle-label">High-Impact News Today</span>
                <Switch checked={form.newsPresent} onChange={v => set("newsPresent", v)} />
              </div>
            </div>

            <hr className="op-hr" />

            <div className="op-section" style={{ marginBottom: 13 }}>
              <div className="op-section-label">Trade Review</div>
              <textarea
                value={form.review || ""}
                onChange={e => set("review", e.target.value)}
                placeholder="What could you have done better?"
                style={{
                  width: "100%", padding: "9px 10px",
                  background: T.SURFACE, border: `1px solid ${T.BORDER}`, borderRadius: 5,
                  color: T.TEXT_FULL, font: "inherit", fontSize: 11, lineHeight: 1.6,
                  resize: "vertical", minHeight: 60, outline: "none",
                  transition: "border-color .15s",
                }}
                onFocus={e => { e.target.style.borderColor = "rgba(201,168,76,0.38)"; }}
                onBlur={e => { e.target.style.borderColor = T.BORDER; }}
              />
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB: MINDSET
        ══════════════════════════════════════════ */}
        {ptab === "mindset" && (
          <>
            <div className="op-section">
              <div className="op-section-label">Emotion</div>
              <div className="op-emotion-grid" style={{ marginBottom: 0 }}>
                {(EMOTIONS || ["Calm","Confident","FOMO","Revenge","Anxious","Greedy","Excited","Fearful","Frustrated","Disciplined"]).map(em => (
                  <button key={em} className={`op-em-chip${form.emotion === em ? " active" : ""}`} onClick={() => set("emotion", form.emotion === em ? "" : em)}>
                    {em}
                  </button>
                ))}
              </div>
            </div>

            <hr className="op-hr" />

            <div className="op-section">
              <div className="op-section-label">Performance Ratings</div>
              <div className="op-stack" style={{ marginBottom: 0 }}>
                {[
                  ["confidence", "Pre-Trade Confidence", T.GREEN],
                  ["discipline", "Discipline", T.BLUE],
                  ["stressLevel", "Stress Level", T.RED],
                ].map(([k, label, color]) => (
                  <div key={k}>
                    <div style={{ fontSize: 9.5, fontWeight: 600, color: T.TEXT_MID, marginBottom: 1 }}>{label}</div>
                    <StarRating value={form[k] || 0} onChange={v => set(k, v)} color={color} />
                    <div style={{ fontSize: 8, color: T.TEXT_DIM, marginTop: 2 }}>
                      {form[k] ? `${form[k]}/5` : "Tap to rate"}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {form.emotion && (form.confidence > 0 || form.discipline > 0) && (
              <div style={{
                margin: "13px 16px 0", padding: "9px 12px",
                background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.14)",
                borderRadius: 5, fontSize: 10, color: T.TEXT_MID, lineHeight: 1.7,
              }}>
                <span style={{ color: T.GOLD, fontSize: 8, fontWeight: 800, letterSpacing: ".12em" }}>AI WILL ANALYZE: </span>
                Emotion: <strong style={{ color: T.GOLD }}>{form.emotion}</strong>
                {form.confidence > 0 && <> · Confidence: <strong style={{ color: T.GREEN }}>{form.confidence}/5</strong></>}
                {form.discipline > 0 && <> · Discipline: <strong style={{ color: T.BLUE }}>{form.discipline}/5</strong></>}
              </div>
            )}

            <div style={{ height: 13 }} />
          </>
        )}

        {/* ── R:R global badge (always visible below plan) ── */}
        {ptab === "plan" && calc.rr && parseFloat(calc.rr) > 0 && (
          <div style={{ padding: "0 16px 0" }}>
            <div className="op-rr-badge">
              <span className="op-rr-label">Risk : Reward</span>
              <span className="op-rr-val">1 : {calc.rr}</span>
              <span className="op-rr-unit">R</span>
            </div>
          </div>
        )}

        {/* ── Submit ── */}
        <div className="op-submit-wrap">
          <button
            className={`op-submit ${form.dir.toLowerCase()}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? <><span style={{ width: 12, height: 12, border: "1.5px solid rgba(255,255,255,0.2)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "cp-spin .75s linear infinite" }} /> Saving…</>
              : <>{form.dir === "BUY" ? "▲" : "▼"} Log {form.dir} {symLabel} to Journal</>
            }
          </button>
          <div className="op-submit-note">Saves directly to your KFX Journal database</div>
        </div>

      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────*/
export default function ChartPage({ activeAccount }) {
  injectCSS();

  const [symbol,     setSymbol]     = useState(SYMBOLS[0]);
  const [loading,    setLoading]    = useState(true);
  const [fadeOut,    setFadeOut]    = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [panelOpen,  setPanelOpen]  = useState(false);

  // Also listen to kfx:accounts-updated for self-contained fallback
  const [resolvedAccount, setResolvedAccount] = useState(activeAccount ?? null);
  useEffect(() => {
    if (activeAccount) { setResolvedAccount(activeAccount); return; }
    const handler = ({ detail }) => {
      const accounts  = detail.tradingAccounts ?? [];
      const activeId  = detail.activeAccountId;
      const found     = accounts.find(a => a.id === activeId) ?? null;
      setResolvedAccount(found);
    };
    window.addEventListener("kfx:accounts-updated", handler);
    return () => window.removeEventListener("kfx:accounts-updated", handler);
  }, [activeAccount]);

  const widgetRef   = useRef(null);
  const scriptRef   = useRef(null);
  const mountedRef  = useRef(false);
  const debounceRef = useRef(null);

  const buildWidget = useCallback(() => {
    const container = widgetRef.current;
    if (!container) return;
    if (scriptRef.current) { clearTimeout(scriptRef.current._fallbackTimer); scriptRef.current = null; }
    container.innerHTML = "";
    setLoading(true); setFadeOut(false);

    const widgetDiv = document.createElement("div");
    widgetDiv.className = "tradingview-widget-container__widget";
    container.appendChild(widgetDiv);

    const script   = document.createElement("script");
    script.src     = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.type    = "text/javascript";
    script.async   = true;
    script.innerHTML = JSON.stringify({
      autosize: true, symbol: symbol.value,
      timezone: "Etc/UTC", theme: "dark", locale: "en",
      hide_top_toolbar: false, hide_legend: false,
      save_image: false, calendar: false, hide_volume: false,
      support_host: "https://www.tradingview.com",
    });

    const dismiss = () => { setFadeOut(true); setTimeout(() => setLoading(false), 420); };
    script.onload = () => setTimeout(dismiss, 800);
    script._fallbackTimer = setTimeout(dismiss, 3500);
    container.appendChild(script);
    scriptRef.current = script;
  }, [symbol]);

  const debouncedBuild = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(buildWidget, 120);
  }, [buildWidget]);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    buildWidget();
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(scriptRef.current?._fallbackTimer);
      if (widgetRef.current) widgetRef.current.innerHTML = "";
      scriptRef.current = null; mountedRef.current = false;
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!mountedRef.current) return;
    debouncedBuild();
  }, [debouncedBuild]);

  const SymBtns = () => SYMBOLS.map(s => (
    <button key={s.value}
      className={`cp-sym-btn${symbol.value === s.value ? " active" : ""}`}
      onClick={() => setSymbol(s)}>
      {s.label}
    </button>
  ));

  const PanelToggle = () => (
    <button
      className={`cp-panel-toggle${panelOpen ? " active" : ""}`}
      onClick={() => setPanelOpen(v => !v)}
      title={panelOpen ? "Close order panel" : "Open order panel"}>
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <rect x="0" y="1.5" width="11" height="1.4" rx=".7" fill="currentColor" opacity=".5"/>
        <rect x="0" y="4.8" width="11" height="1.4" rx=".7" fill="currentColor"/>
        <rect x="0" y="8.1" width="7"  height="1.4" rx=".7" fill="currentColor" opacity=".5"/>
      </svg>
      {panelOpen ? "Close Panel" : "Quick Log"}
    </button>
  );

  return (
    <div className="cp-page">

      {/* Desktop bar */}
      <div className="cp-bar">
        <SymBtns />
        <span className="cp-bar-spacer" />
        <div className="cp-divider" />
        <PanelToggle />
      </div>

      {/* Mobile bar — row 1: symbols, row 2: toggle */}
      <div className="cp-bar-mobile">
        <div className="cp-bar-row" style={{ gap: 2 }}>
          <SymBtns />
        </div>
        <div className="cp-bar-row" style={{ justifyContent: "space-between", padding: "0 12px" }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".10em", textTransform: "uppercase", color: T.TEXT_DIM }}>
            KFX Chart
          </span>
          <PanelToggle />
        </div>
      </div>

      {/* Dev banner */}
      {showBanner && (
        <div className="cp-dev-banner">
          <span className="cp-dev-dot" aria-hidden="true" />
          <span className="cp-dev-label">Dev</span>
          <span className="cp-dev-text">Chart page is under active development.</span>
          <button className="cp-dev-dismiss" onClick={() => setShowBanner(false)} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* Body */}
      <div className="cp-body">

        {/* Tap-outside dim overlay — mobile only, shown when panel is open */}
        {panelOpen && (
          <div
            onClick={() => setPanelOpen(false)}
            style={{
              display: "none", // shown via media query below via inline JS detection
              position: "absolute", inset: 0, zIndex: 19,
              background: "rgba(0,0,0,0.45)",
            }}
            className="cp-panel-overlay"
          />
        )}

        {/* Chart */}
        <div className="cp-widget-wrap">
          {loading && (
            <div className={`cp-loader${fadeOut ? " fade-out" : ""}`}>
              <div className="cp-loader-ring" />
              <div className="cp-loader-label">Loading chart…</div>
            </div>
          )}
          <div ref={widgetRef} className="cp-tv-container tradingview-widget-container" />
        </div>

        {/* Order panel */}
        <OrderPanel
          open={panelOpen}
          activeAccount={resolvedAccount}
          chartSymbol={symbol}
        />

      </div>
    </div>
  );
}